export { init, Relia, identify, track, globalClient } from './browser/index'
export type { ReliaClient, ReliaOptions, SessionEvent, SessionEventType } from './core/types'

export interface AgentRunStep {
  type: "tool" | "model"
  name: string
  detail?: string
  offset_ms: number
  duration_ms: number
  tokens?: number
  tokens_in?: number
  tokens_out?: number
  provider?: string
  model?: string
  status: "ok" | "error"
  input?: string
  output?: string
}

function extractPromptFromSettings(settings: any): string {
  if (typeof settings?.prompt === "string" && settings.prompt.trim()) {
    return settings.prompt.slice(0, 2000)
  }
  const messages = Array.isArray(settings?.messages) ? settings.messages : []
  if (messages.length > 0) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m?.role === "user") {
        if (typeof m.content === "string") return m.content.slice(0, 2000)
        if (Array.isArray(m.content)) {
          const text = m.content
            .filter((c: any) => c?.type === "text" && typeof c?.text === "string")
            .map((c: any) => c.text)
            .join(" ")
          if (text.trim()) return text.slice(0, 2000)
        }
      }
    }
  }
  return ""
}

function extractAnswerFromEvent(event: any, collectedSteps: any[]): string {
  if (typeof event?.text === "string" && event.text.trim()) {
    return event.text.slice(0, 4000)
  }
  if (Array.isArray(event?.response?.messages)) {
    for (let i = event.response.messages.length - 1; i >= 0; i--) {
      const m = event.response.messages[i]
      if (m?.role === "assistant") {
        if (typeof m.content === "string") return m.content.slice(0, 4000)
        if (Array.isArray(m.content)) {
          const text = m.content
            .filter((c: any) => c?.type === "text" && typeof c?.text === "string")
            .map((c: any) => c.text)
            .join("\n")
          if (text.trim()) return text.slice(0, 4000)
        }
      }
    }
  }
  const allSteps = Array.isArray(event?.steps) && event.steps.length > 0 ? event.steps : collectedSteps
  const textParts: string[] = []
  for (const s of allSteps) {
    if (typeof s?.text === "string" && s.text.trim()) {
      textParts.push(s.text.trim())
    }
  }
  return textParts.join("\n").slice(0, 4000)
}

export function traceStreamText<T extends Record<string, any>>(
  settings: T,
  options?: { agentId?: string; agentName?: string; endpoint?: string },
): T {
  const env = typeof process !== "undefined" ? process.env || {} : {}
  const agentId = options?.agentId || env.RELIA_AGENT_ID || "posthog-ai"
  const agentName = options?.agentName || env.RELIA_AGENT_NAME || agentId
  const endpoint = options?.endpoint || env.RELIA_AGENT_ENDPOINT || "http://127.0.0.1:8000/api/v1/agents/runs"

  const originalOnFinish = settings.onFinish
  const originalOnStepStart = settings.onStepStart
  const originalOnStepFinish = settings.onStepFinish
  const startedAt = Date.now()
  const stepStarts = new Map<number, number>()
  const stepDurations = new Map<number, number>()
  const collectedSteps: any[] = []

  const next: any = { ...settings }

  let stepIdx = 0
  next.onStepStart = (ev: any) => {
    const key = ev?.stepNumber ?? stepIdx
    stepStarts.set(key, Date.now())
    if (typeof originalOnStepStart === "function") originalOnStepStart(ev)
  }

  next.onStepFinish = (ev: any) => {
    if (ev) {
      collectedSteps.push(ev)
      const key = ev?.stepNumber ?? stepIdx
      const start = stepStarts.get(key)
      if (start != null) {
        stepDurations.set(key, Math.max(0, Date.now() - start))
        stepStarts.delete(key)
      }
      stepIdx++
    }
    if (typeof originalOnStepFinish === "function") originalOnStepFinish(ev)
  }

  next.onFinish = async (event: any) => {
    try {
      const usage = event?.totalUsage ?? event?.usage ?? {}
      const allSteps = Array.isArray(event?.steps) && event.steps.length > 0 ? event.steps : collectedSteps
      const stepsOut: AgentRunStep[] = []

      for (let idx = 0; idx < allSteps.length; idx++) {
        const s = allSteps[idx]
        const dur = stepDurations.get(s?.stepNumber ?? idx) ?? 0
        const parts: any[] = Array.isArray(s?.parts) ? s.parts : Array.isArray(s?.content) ? s.content : []
        const calls = parts.length
          ? parts.filter((p) => p?.type === "tool-call")
          : Array.isArray(s?.toolCalls)
            ? s.toolCalls
            : []
        const results = parts.length
          ? parts.filter((p) => p?.type === "tool-result")
          : Array.isArray(s?.toolResults)
            ? s.toolResults
            : []
        const errors = parts.length ? parts.filter((p) => p?.type === "tool-error") : []

        if (calls.length > 0 || errors.length > 0) {
          const byId = new Map(results.map((r: any) => [r?.toolCallId, r]))
          calls.forEach((call: any, i: number) => {
            const res = parts.length ? byId.get(call?.toolCallId) : results[i]
            const rawIn = call?.args ?? call?.input ?? ""
            const rawOut = res?.output ?? res?.result ?? ""
            const inStr = typeof rawIn === "object" ? JSON.stringify(rawIn) : String(rawIn)
            const outStr = typeof rawOut === "object" ? JSON.stringify(rawOut) : String(rawOut)
            stepsOut.push({
              type: "tool",
              name: String(call?.toolName ?? "tool").slice(0, 100),
              detail: (outStr || inStr).slice(0, 500),
              offset_ms: 0,
              duration_ms: dur,
              status: "ok",
              input: inStr.slice(0, 10000),
              output: outStr.slice(0, 10000),
            })
          })
          for (const err of errors) {
            const rawIn = err?.args ?? err?.input ?? ""
            const inStr = typeof rawIn === "object" ? JSON.stringify(rawIn) : String(rawIn)
            const errDetail = String(err?.error?.message ?? err?.error ?? "")
            stepsOut.push({
              type: "tool",
              name: String(err?.toolName ?? "tool").slice(0, 100),
              detail: errDetail.slice(0, 500),
              offset_ms: 0,
              duration_ms: dur,
              status: "error",
              input: inStr.slice(0, 10000),
              output: errDetail.slice(0, 10000),
            })
          }
          continue
        }

        const modelInput = extractPromptFromSettings(settings)
        const modelOutput = String(s?.text ?? "")
        stepsOut.push({
          type: "model",
          name: String(s?.model?.modelId ?? settings?.model?.modelId ?? "model").slice(0, 100),
          detail: modelOutput.slice(0, 300) || modelInput.slice(0, 300),
          offset_ms: 0,
          duration_ms: dur,
          tokens: usage.totalTokens ?? 0,
          tokens_in: usage.inputTokens ?? 0,
          tokens_out: usage.outputTokens ?? 0,
          provider: s?.model?.provider ?? settings?.model?.provider ?? "",
          model: s?.model?.modelId ?? settings?.model?.modelId ?? "",
          status: "ok",
          input: modelInput.slice(0, 10000),
          output: modelOutput.slice(0, 10000),
        })
      }

      const promptText = extractPromptFromSettings(settings)
      const answerText = extractAnswerFromEvent(event, collectedSteps)
      const modelId = String(
        event?.response?.modelId ?? event?.model?.modelId ?? settings?.model?.modelId ?? "",
      ).slice(0, 200)

      const payload = {
        agent_id: agentId,
        name: agentName,
        service_name: agentName,
        primary_model: modelId,
        version: "1.0.0",
        endpoint: endpoint.replace(/\/runs$/, ""),
        duration_ms:
          typeof event?.latencyInMs === "number"
            ? Math.round(event.latencyInMs)
            : Math.max(0, Date.now() - startedAt),
        tokens_in: usage.inputTokens ?? 0,
        tokens_out: usage.outputTokens ?? 0,
        status: event?.error ? "error" : "ok",
        prompt: promptText,
        answer: answerText,
        steps: stepsOut,
      }

      const targetEndpoint = endpoint.replace(/^http:\/\/localhost(?=[:/])/i, "http://127.0.0.1")
      console.log(`[relia] reporting agent run for "${agentId}" (${stepsOut.length} steps) -> ${targetEndpoint}`)
      fetch(targetEndpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((res) => {
          console.log(`[relia] agent run delivery status: ${res.status}`)
        })
        .catch((err) => {
          console.warn(`[relia] agent run delivery error:`, err?.message ?? err)
        })
    } catch (err) {
      console.warn(`[relia] agent telemetry error:`, err)
    }

    if (typeof originalOnFinish === "function") {
      await originalOnFinish(event)
    }
  }

  return next as T
}

import Relia from './browser/index'
export default Relia
